// The landscape as the original draws it: the world bake's tiles (bake.ts: the sim's
// `sim::view::scene` quads drawn into the MainScene layers, cut into 2048x2048 tiles with the
// state grid in their alpha and the original's mip chain) as `WS_Geometry` cells, with the
// landscape shaders over the damage pass's target (damage.ts), the soil texture, the building
// fades (buildings.ts), the girders (girders.ts) and the water line (docs/platform/ClientShell.md
// "Landscape", docs/platform/RenderFidelity.md "Landscape damage", "Baked tiles").
import * as THREE from 'three';
import { ktx2Texture } from '../../tools/viewer/ktx2.js';
import { assetBytes, assetJson, assetUrl } from '../assets.ts';
import { TIER_BG, TIER_FG, TIER_MID, type SceneView } from '../sim/view.ts';
import { bakeTiles, CELL, CELLS_X, CELLS_Y, mainSceneLists, BAKE_H, BAKE_W } from './bake.ts';
import { DamageLayer } from './damage.ts';
import { GirderLayer } from './girders.ts';

type SpritesJson = { pages: Record<string, { ktx2?: string; png?: string }>; sprites: Record<string, { page: string; uv: number[]; rect: number[] }> };

let spritesJson: Promise<SpritesJson> | null = null;
export function worldSprites(): Promise<SpritesJson> {
  spritesJson ??= assetJson<SpritesJson>('world/sprites.json');
  return spritesJson;
}

/** A WAD atlas page as a texture (KTX2 with the original DXT blocks). The error handling stays
 * outside a `try` block: Rollup keeps every export of a namespace passed to a call inside one.
 * Also the lobby preview's pages (preview.ts). */
export function pageTexture(renderer: THREE.WebGLRenderer, path: string): Promise<THREE.Texture | null> {
  return (async () => {
    const rec = (await worldSprites()).pages[path];
    if (!rec?.ktx2) return null;
    const { texture } = ktx2Texture(THREE, renderer, await assetBytes(rec.ktx2));
    texture.colorSpace = THREE.NoColorSpace;
    return texture;
  })().catch(e => { console.warn('page', path, e); return null; });
}

type XomGraph = { graph: { objects: Record<string, { class: string; png?: string | null; width?: number; height?: number }> } };

/** The image of a XOM bitmap descriptor (`<bundle>/<name>.json`) as a texture: linear, no
 * mips, the given wrap, raw values (no colour decoding), rows as stored. */
export async function xomTexture(path: string, wrap: THREE.Wrapping): Promise<{ texture: THREE.Texture; width: number; height: number }> {
  const desc = await assetJson<XomGraph>(path);
  const img = Object.values(desc.graph.objects).find(o => o.class === 'XImage' && o.png);
  if (!img?.png) throw new Error(`${path}: no image`);
  const texture = await new THREE.TextureLoader().loadAsync(assetUrl(img.png));
  texture.flipY = false;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = texture.wrapT = wrap;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return { texture, width: img.width ?? texture.image.width, height: img.height ?? texture.image.height };
}

// `WSLandscapeV` (GLSL of ShaderProgLibrary.xom): textureUV and worldPos are the vertex's.
const VERT = /* glsl */ `
out vec2 vUv;
out vec2 vWorld;
void main() {
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xy;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

// The landscape fragment shaders (GLSL `#version 330` of ShaderProgLibrary.xom) on the raw colour
// values. The tile is sampled with its own state (nearest, nearest mipmap) and the tile's
// `GL_TEXTURE_LOD_BIAS` -1 as the shader bias; the alpha byte is `uint(a * 255)`: a3 = bits 0-2
// (drawn as a3 / 7), IO key bits 3-5, indestructible bit 6, collision bit 7.
const COMMON = /* glsl */ `
precision highp float;
precision highp int;
precision highp usampler2D;
uniform sampler2D tileColour;
uniform sampler2D sDamage;
uniform float waterLevel;
in vec2 vUv;
in vec2 vWorld;
const float MAX_ALPHA_MULT = 1.0 / 7.0;
vec3 rim(vec3 c, vec4 d) {
  vec3 blend = vec3(98.0 + 80.0, 90.0 + 80.0, 67.0 + 80.0) / 255.0;
  vec3 base = vec3(d.g);
  vec3 overlay = mix(2.0 * base * blend, 1.0 - 2.0 * (1.0 - base) * (1.0 - blend), step(0.5, base));
  return mix(c, overlay, d.b);
}`;

// `WSLandscapeBGF`: the water line and a3 only. (Here and in FG the tile is sampled before the
// first discard, so its level of detail never depends on discarded neighbours.)
const BG_FRAG = /* glsl */ `${COMMON}
void main() {
  vec4 landColour = texture(tileColour, vUv, -1.0);
  if (vWorld.y < waterLevel) discard;
  uint alphaChannel = uint(landColour.a * 255.0);
  float alphaValue = float(alphaChannel & 7u) * MAX_ALPHA_MULT;
  if (alphaValue == 0.0) discard;
  gl_FragColor = vec4(landColour.rgb, alphaValue);
}`;

// `WSLandscapeF` (MID): collidable, destructible pixels: R >= 1 clears outside a building;
// 0.5 < R clears inside a building (`bIsBuilding`: an IO key and a3 = 7 in `backTileColour`, the
// BG tier's tile of the cell or the MID tile itself) and draws the soil (`sDamagedLand`, 1024
// units a tile) with the land's alpha times `StandardSoilEnabled`; below that the rim. A client
// without the damage history (`history` 0: it joined mid-match) cuts with the sim's collision
// bitmap (= R >= 0.5) instead. Alpha 0 writes nothing with the land's blend, so it discards.
const MID_FRAG = /* glsl */ `${COMMON}
uniform sampler2D backTileColour;
uniform sampler2D sDamagedLand;
uniform usampler2D landMask;
uniform int StandardSoilEnabled;
uniform int history;
void main() {
  vec2 damageUV = vec2((vWorld.x + 4096.0) / 8192.0, (vWorld.y + 2048.0) / 4096.0);
  vec4 landColour = texture(tileColour, vUv, -1.0);
  uint alphaChannel = uint(landColour.a * 255.0);
  float alphaValue = float(alphaChannel & 7u) * MAX_ALPHA_MULT;
  if (vWorld.y < waterLevel) alphaValue = 0.0;
  vec4 backLandColour = texture(backTileColour, vUv, -1.0);
  uint backAlphaChannel = uint(backLandColour.a * 255.0);
  float backAlphaValue = float(backAlphaChannel & 7u) * MAX_ALPHA_MULT;
  uint buildingIndex = (alphaChannel & 56u) >> 3;
  bool bIsBuilding = buildingIndex != 0u && backAlphaValue == 1.0;
  bool bCollidable = (alphaChannel & 128u) > 0u;
  bool bIndestructible = (alphaChannel & 64u) > 0u;
  if (!bIndestructible && bCollidable) {
    if (history == 0) {
      ivec2 p = ivec2(floor(vWorld)) + ivec2(4096, 2048);
      bool inside = p.x >= 0 && p.y >= 0 && p.x < 8192 && p.y < 4096;
      if (!inside || ((texelFetch(landMask, ivec2(p.x >> 3, p.y), 0).r >> uint(p.x & 7)) & 1u) == 0u) alphaValue = 0.0;
    } else {
      vec4 damageValue = texture(sDamage, damageUV);
      if (damageValue.r >= 1.0 && !bIsBuilding) {
        alphaValue = 0.0;
      } else if (damageValue.r > 0.5) {
        if (bIsBuilding) alphaValue = 0.0;
        alphaValue = alphaValue * float(StandardSoilEnabled);
        landColour.rgb = texture(sDamagedLand, damageUV * vec2(8.0, 4.0)).rgb;
      } else {
        landColour.rgb = rim(landColour.rgb, damageValue);
      }
    }
  }
  if (alphaValue == 0.0) discard;
  gl_FragColor = vec4(landColour.rgb, alphaValue);
}`;

// `WSLandscapeFGF`: destructible pixels drop where R > 0.4 and overlay the rim where B > 0; the
// alpha of a pixel with an IO key is multiplied by `buildingFades[key]`.
const FG_FRAG = /* glsl */ `${COMMON}
uniform float buildingFades[15];
void main() {
  vec4 landColour = texture(tileColour, vUv, -1.0);
  if (vWorld.y < waterLevel) discard;
  vec2 damageUV = vec2((vWorld.x + 4096.0) / 8192.0, (vWorld.y + 2048.0) / 4096.0);
  vec4 damageValue = texture(sDamage, damageUV);
  uint alphaChannel = uint(landColour.a * 255.0);
  float alphaValue = float(alphaChannel & 7u) * MAX_ALPHA_MULT;
  if (alphaValue == 0.0) discard;
  float alphaLevel = 1.0;
  uint buildingIndex = (alphaChannel & 56u) >> 3;
  if (buildingIndex != 0u) alphaLevel = buildingFades[buildingIndex];
  if ((alphaChannel & 64u) == 0u) {
    if (damageValue.r > 0.4) discard;
    if (damageValue.b > 0.0) landColour.rgb = rim(landColour.rgb, damageValue);
  }
  gl_FragColor = vec4(landColour.rgb, alphaLevel * alphaValue);
}`;

const FRAG: Record<number, string> = { [TIER_BG]: BG_FRAG, [TIER_MID]: MID_FRAG, [TIER_FG]: FG_FRAG };

export const LAND_RENDER_ORDER: Record<number, number> = { [TIER_BG]: 100, [TIER_MID]: 200, [TIER_FG]: 600 };
/** The girders draw in the MID layer after its cells (`FWDP_Transparent:WS_LandscapeMID`). */
const GIRDER_ORDER = 299;

/** A cell's quad (`WS_Geometry::__CreateXomGeometry` 0xe6fa40): x from `cx * 2048 - 4096`, the
 * bottom row from -2048 and every later row `row + 0.5` lower (`2048 row - 2048 - (row + 0.5)`:
 * the upper half of the world is drawn 1.5 units below its bake); vertices TL (0, 1), BL (0, 0),
 * BR (1, 0), TR (1, 1), indices 2, 1, 0, 3, 2, 0. */
function cellGeometry(cell: number): THREE.BufferGeometry {
  const cx = cell % CELLS_X, cy = Math.floor(cell / CELLS_X);
  const x0 = cx * CELL - BAKE_W / 2;
  const y0 = cy === 0 ? -BAKE_H / 2 : Math.fround(CELL * cy - BAKE_H / 2 - Math.fround(cy + 0.5));
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([x0, y0 + CELL, 0, x0, y0, 0, x0 + CELL, y0, 0, x0 + CELL, y0 + CELL, 0], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 1, 1], 2));
  g.setIndex([2, 1, 0, 3, 2, 0]);
  return g;
}

/** A three.js texture over a GL texture of the bake (sampled with the GL texture's own state;
 * three.js never uploads or deletes it). */
function wrapTexture(renderer: THREE.WebGLRenderer, t: WebGLTexture): THREE.Texture {
  const tex = new THREE.Texture();
  const props: unknown = renderer.properties.get(tex);
  if (props && typeof props === 'object') Object.assign(props, { __webglTexture: t });
  return tex;
}

export class LandLayer {
  /** BG and MID tiers (behind the worms). */
  back = new THREE.Group();
  /** FG tier (in front of the worms). */
  front = new THREE.Group();
  mask: THREE.DataTexture;
  /** The damage pass target the MID and FG tiers read. */
  damage = new DamageLayer();
  /** `buildingFades` of the FG geomset (buildings.ts writes it). */
  readonly fades = { value: new Float32Array(15).fill(1) };
  /** The baked tiles per tier, 8 cells each from the bottom-left (null: no tile). */
  tiles = new Map<number, (WebGLTexture | null)[]>();
  /** The last bake: atlas page loading, the bake itself (ms), the tiles and their GPU bytes. */
  bakeStats: { pagesMs: number; bakeMs: number; tiles: number; bytes: number } | null = null;
  private maskData = new Uint8Array(1024 * 4096);
  private maskUniform: { value: THREE.DataTexture };
  private waterUniform = { value: -1e9 };
  private damageUniform = { value: this.damage.target.texture };
  private soilUniform: { value: THREE.Texture | null } = { value: null };
  private soilEnabled = { value: 1 };
  private history = { value: 0 };
  /** The bake's state grid (`sim::view::land(2)`) until a bake used it. */
  private state: Uint8Array | null = null;
  /** The scene and atlas pages of the match while no bake with the state grid ran. */
  private pending: { scene: SceneView; pages: (THREE.Texture | null)[]; pagesMs: number } | null = null;
  /** The renderer of the bake (its GL holds the tiles). */
  private renderer: THREE.WebGLRenderer | null = null;
  private generation = 0;
  /** The girder geomset (girders.ts). */
  girders = new GirderLayer(this.damageUniform, this.soilUniform, this.waterUniform, GIRDER_ORDER);

  constructor() {
    this.mask = new THREE.DataTexture(this.maskData, 1024, 4096, THREE.RedIntegerFormat, THREE.UnsignedByteType);
    this.mask.internalFormat = 'R8UI';
    this.mask.minFilter = THREE.NearestFilter;
    this.mask.magFilter = THREE.NearestFilter;
    this.mask.unpackAlignment = 1;
    this.mask.flipY = false;
    this.mask.generateMipmaps = false;
    this.mask.needsUpdate = true;
    this.maskUniform = { value: this.mask };
    this.back.add(this.girders.group);
  }

  /** The theme's soil (`Bundles/<theme>/DamagedLand`, Soil.tga: repeat, linear, no mips) and the
   * girder textures. */
  async loadTextures(theme: string | null): Promise<void> {
    const [soil] = await Promise.all([
      xomTexture(`xom/DataLinux/Bundles/${theme ?? 'China'}/DamagedLand.json`, THREE.RepeatWrapping),
      this.girders.load(),
    ]);
    this.soilUniform.value = soil.texture;
  }

  /** The per-frame landscape state: `StandardSoilEnabled` and whether the damage target holds the
   * whole history. */
  setState(soilEnabled: boolean): void {
    this.soilEnabled.value = soilEnabled ? 1 : 0;
    this.history.value = this.damage.history ? 1 : 0;
  }

  /** The collision bitmap (`sim::view::land(0)`): 4096 rows of 1024 bytes, world y = row - 2048. */
  setMask(bytes: ArrayBuffer): void {
    this.maskData.set(new Uint8Array(bytes));
    this.mask.needsUpdate = true;
  }

  /** The bake's state grid (`sim::view::land(2)`): 4096 rows of 8192 bytes, world y = row - 2048,
   * world x = column - 4096 (collision 0x80, indestructible 0x40, IO key 0x38). A bake that ran
   * without it runs again. */
  setBakeState(bytes: ArrayBuffer): void {
    if (bytes.byteLength !== BAKE_W * BAKE_H) return;
    this.state = new Uint8Array(bytes);
    if (this.pending) this.bake();
  }

  /** The water line (world y): land below it is not drawn. */
  setWater(level: number): void {
    this.waterUniform.value = level;
  }

  /** Loads the scene's atlas pages and bakes the tiles. */
  async build(scene: SceneView, renderer: THREE.WebGLRenderer): Promise<void> {
    const generation = ++this.generation;
    this.releasePages();
    const t0 = performance.now();
    const used = new Set(scene.quads.map(q => q.page));
    const pages = await Promise.all(scene.pages.map((p, i) => (used.has(i) ? pageTexture(renderer, p.path) : Promise.resolve(null))));
    if (generation !== this.generation) {
      for (const t of pages) t?.dispose();
      return;
    }
    this.renderer = renderer;
    this.pending = { scene, pages, pagesMs: performance.now() - t0 };
    this.bake();
  }

  /** The MainScene layers into tiles (bake.ts) and the cells drawing them. The atlas pages are
   * released once a bake had the state grid. */
  private bake(): void {
    const p = this.pending;
    const renderer = this.renderer;
    if (!p || !renderer) return;
    this.disposeTiles();
    const r = bakeTiles(renderer, mainSceneLists(p.scene), p.pages, this.state);
    this.tiles = r.tiles;
    const count = [...r.tiles.values()].reduce((n, cells) => n + cells.filter(Boolean).length, 0);
    this.bakeStats = { pagesMs: p.pagesMs, bakeMs: r.ms, tiles: count, bytes: r.bytes };
    console.info(`land bake: ${count} tiles, ${(r.bytes / 2 ** 20).toFixed(1)} MiB, pages ${p.pagesMs.toFixed(0)} ms, bake ${r.ms.toFixed(0)} ms`);
    this.buildCells(renderer);
    if (this.state) {
      this.state = null;
      this.releasePages();
    }
  }

  /** Every tier's cells with a tile, in cell order (the geomset's draw order). */
  private buildCells(renderer: THREE.WebGLRenderer): void {
    const bg = this.tiles.get(TIER_BG);
    for (const tier of [TIER_BG, TIER_MID, TIER_FG]) {
      const cells = this.tiles.get(tier);
      if (!cells) continue;
      cells.forEach((tile, cell) => {
        if (!tile) return;
        const uniforms: Record<string, THREE.IUniform> = {
          tileColour: { value: wrapTexture(renderer, tile) }, sDamage: this.damageUniform, waterLevel: this.waterUniform,
        };
        if (tier === TIER_MID) {
          // XLandscapeGeomSet::pBackTextureHandles: the BG tile of the cell, else the MID tile.
          uniforms.backTileColour = { value: wrapTexture(renderer, bg?.[cell] ?? tile) };
          Object.assign(uniforms, { sDamagedLand: this.soilUniform, landMask: this.maskUniform, StandardSoilEnabled: this.soilEnabled, history: this.history });
        } else if (tier === TIER_FG) {
          uniforms.buildingFades = this.fades;
        }
        const mat = new THREE.ShaderMaterial({
          vertexShader: VERT, fragmentShader: FRAG[tier], uniforms,
          transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(cellGeometry(cell), mat);
        mesh.renderOrder = LAND_RENDER_ORDER[tier] + cell / 100;
        mesh.frustumCulled = false;
        (tier === TIER_FG ? this.front : this.back).add(mesh);
      });
    }
  }

  private disposeTiles(): void {
    for (const g of [this.back, this.front]) {
      for (const m of [...g.children]) {
        if (!(m instanceof THREE.Mesh)) continue;
        g.remove(m);
        m.geometry.dispose();
        if (m.material instanceof THREE.Material) m.material.dispose();
      }
    }
    const gl = this.renderer?.getContext();
    for (const cells of this.tiles.values()) for (const t of cells) if (t) gl?.deleteTexture(t);
    this.tiles = new Map();
  }

  private releasePages(): void {
    for (const t of this.pending?.pages ?? []) t?.dispose();
    this.pending = null;
  }
}
